import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

export default function useRetailItemList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refRetailItemsTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'retailItem',
      label: 'Retail Item',
      sortable: true,
    },
    {
      key: 'category',
      label: 'Category',
      sortable: false,
    },
    {
      key: 'brand',
      label: 'Brand',
      sortable: true,
    },
    {
      key: 'unitPrice',
      label: 'Unit Price',
      sortable: true,
    },
    {
      key: 'createdAt',
      label: 'Created Date',
      sortable: true,
    },
    { key: 'actions', label: '' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalRetailItems = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'createdAt')
  const isSortDirDesc = ref(route.query.sortDesc ? (route.query.sortDesc !== 'false') : true)

  const categoryFilter = ref([])
  if (Array.isArray(route.query.category)) {
    categoryFilter.value = route.query.category
  } else if (route.query.category) {
    categoryFilter.value = [route.query.category]
  }

  const currentDataLength = ref(0)
  const itemListData = ref([])
  const toDeletedID = ref(null)
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refRetailItemsTable.value ? refRetailItemsTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalRetailItems.value,
    }
  })

  const refetchData = () => {
    refRetailItemsTable.value.refresh()
  }

  const fetchRetailItems = () => {
    store
      .dispatch('app-product-retail-item/fetchRetailItems', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        category: categoryFilter.value,
      },
      { root: true })
      .then(response => {
        const { retailItems, total } = response.data
        itemListData.value = retailItems
        currentDataLength.value = retailItems.length
        totalRetailItems.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              category: categoryFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteRetailItem = () => {
    store
      .dispatch('app-product-retail-item/deleteRetailItem', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        fetchRetailItems()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  let timer = null
  watch([searchQuery], (current, prev) => {
    const currentVal = current[0]
    const prevVal = prev[0]
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      if ((!currentVal && prevVal) || (currentVal && currentVal.length > 2)) {
        currentPage.value = 1
        fetchRetailItems()
      }
    }, 1000)
  }, { deep: true })

  watch([sortBy, perPage, isSortDirDesc, currentPage], () => {
    fetchRetailItems()
  }, { deep: true })

  watch([categoryFilter], () => {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      currentPage.value = 1
      fetchRetailItems()
    }, 1000)
  }, { deep: true })

  fetchRetailItems()

  return {
    fetchRetailItems,
    deleteRetailItem,
    tableColumns,
    perPage,
    currentPage,
    totalRetailItems,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refRetailItemsTable,

    refetchData,

    categoryFilter,

    currentDataLength,
    itemListData,
    toDeletedID,
  }
}
